import { useState, useCallback, useEffect, useRef } from 'react';
import { kbTagService } from '@/services/api';
import type { KBTagDefinition } from '@/common-types';

export interface UseKBTagsReturn {
  tagDefinitions: KBTagDefinition[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useKBTags(enabled: boolean): UseKBTagsReturn {
  const [tagDefinitions, setTagDefinitions] = useState<KBTagDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const tags = await kbTagService.listTags();
      setTagDefinitions(tags);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load KB tag definitions');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!fetchingRef.current) {
      fetchingRef.current = true;
      void refresh().finally(() => { fetchingRef.current = false; });
    }
  }, [refresh]);

  return { tagDefinitions, loading, error, refresh };
}

export default useKBTags;
